import { Copy, ExternalLink, Mail, Save, X } from "lucide-react";
import { useState } from "react";
import type { DraftFileMeta, FormDraftSnapshot } from "../../lib/formProgress";

export interface SaveDraftResult {
  draft: FormDraftSnapshot;
  resumeLink: string;
}

export interface ResumeEmailResult {
  emailSent: boolean;
  message?: string;
}

interface SaveAndResumeLaterProps {
  formTitle: string;
  getDefaultEmail?: () => string;
  onSaveDraft: () => Promise<SaveDraftResult>;
  restoreNotice?: string;
  savedFiles?: DraftFileMeta[];
}

export function SaveAndResumeLater({
  formTitle,
  getDefaultEmail,
  onSaveDraft,
  restoreNotice = "",
  savedFiles = [],
}: SaveAndResumeLaterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [saveResult, setSaveResult] = useState<SaveDraftResult | null>(null);
  const [emailResult, setEmailResult] = useState<ResumeEmailResult | null>(null);
  const [copyStatus, setCopyStatus] = useState("");
  const [saveError, setSaveError] = useState("");

  function openModal() {
    setEmail(getDefaultEmail?.() || "");
    setEmailError("");
    setSaveError("");
    setCopyStatus("");
    setIsOpen(true);
  }

  async function handleEmailSubmit() {
    const trimmedEmail = email.trim();
    setEmailError("");
    setCopyStatus("");

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setEmailError("Enter a valid email address.");
      return;
    }

    setIsSaving(true);
    let draftResult: SaveDraftResult;
    try {
      draftResult = await onSaveDraft();
      setSaveResult(draftResult);
    } catch {
      setSaveError("We could not save your progress. Please try again.");
      setIsSaving(false);
      return;
    }

    try {
      const response = await fetch("/api/forms/resume-link", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: trimmedEmail,
          formKey: draftResult.draft.formKey,
          formTitle,
          resumeLink: draftResult.resumeLink,
        }),
      });
      const data = (await response.json().catch(() => ({}))) as ResumeEmailResult;
      setEmailResult({
        emailSent: Boolean(response.ok && data.emailSent),
        message: data.message,
      });
    } catch {
      setEmailResult({
        emailSent: false,
        message: "Email delivery failed. Please copy your resume link below.",
      });
    } finally {
      setIsSaving(false);
    }
  }

  async function handleCopyResumeLink() {
    if (!saveResult) return;
    try {
      await navigator.clipboard.writeText(saveResult.resumeLink);
      setCopyStatus("Resume link copied.");
    } catch {
      setCopyStatus("Copy the link from the field above.");
    }
  }

  return (
    <>
      <div className="fixed inset-x-3 bottom-3 z-40 flex justify-center sm:justify-end">
        <button
          type="button"
          onClick={openModal}
          className="focus-ring inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-lg border border-[#FF6A2B]/45 bg-[#050505] px-5 text-sm font-extrabold text-[#FF6A2B] shadow-[0_18px_50px_rgba(5,5,5,.32)] transition hover:bg-[#16120a] sm:w-auto"
        >
          <Save className="size-4" aria-hidden="true" />
          Save My Progress
        </button>
      </div>

      <div className="grid gap-3">
        <div className="rounded-lg border border-[#D94A16]/22 bg-[#050505] p-5 text-[#fff9ef] shadow-[0_22px_60px_rgba(45,32,8,.12)]">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="grid gap-1">
              <p className="text-sm font-semibold leading-7 text-[#fff9ef]/82">
                Need more time? You can save your progress and return later without losing your work.
              </p>
              <p className="text-xs font-bold leading-6 text-[#FF6A2B]">You can return later using your emailed resume link.</p>
            </div>
            <button
              type="button"
              onClick={openModal}
              className="focus-ring inline-flex min-h-11 shrink-0 items-center justify-center gap-2 rounded-lg bg-[#FF6A2B] px-4 text-sm font-extrabold text-[#050505] transition hover:bg-[#FF6A2B]"
            >
              <Save className="size-4" aria-hidden="true" />
              Save My Progress
            </button>
          </div>
        </div>

        {restoreNotice ? (
          <p className="rounded-lg border border-[#D94A16]/22 bg-white px-5 py-4 text-sm font-bold leading-7 text-[#5c5246]" role="status">
            {restoreNotice}
          </p>
        ) : null}

        {savedFiles.length ? (
          <p className="rounded-lg border border-[#D94A16]/22 bg-white px-5 py-4 text-sm font-semibold leading-7 text-[#5c5246]">
            Saved file selections: {savedFiles.map((file) => file.fileName).join(", ")}
          </p>
        ) : null}
      </div>

      {isOpen ? (
        <div className="fixed inset-0 z-50 grid place-items-end bg-[#050505]/58 p-3 sm:place-items-center" role="presentation">
          <section
            aria-labelledby="save-progress-title"
            aria-modal="true"
            className="max-h-[92dvh] w-full max-w-2xl overflow-y-auto rounded-lg border border-[#FF6A2B]/35 bg-white p-5 text-[#16120a] shadow-[0_30px_90px_rgba(5,5,5,.36)]"
            role="dialog"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.18em] text-[#D94A16]">Save Progress</p>
                <h2 id="save-progress-title" className="mt-2 font-display text-3xl font-bold text-[#050505]">
                  Email your resume link
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="focus-ring grid size-10 shrink-0 place-items-center rounded-lg border border-[#D94A16]/24 text-[#5c5246] transition hover:bg-[#fff9ef]"
                aria-label="Close save progress dialog"
              >
                <X className="size-4" aria-hidden="true" />
              </button>
            </div>

            {!saveResult ? (
              <div className="mt-5 grid gap-4">
                <p className="text-sm font-semibold leading-7 text-[#5c5246]">
                  Enter your email address and Kreatethedate will send you a private resume link for this form.
                </p>
                <div>
                  <label htmlFor="resume-email" className="text-sm font-black text-[#16120a]">
                    Email address
                  </label>
                  <input
                    id="resume-email"
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.currentTarget.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        void handleEmailSubmit();
                      }
                    }}
                    aria-invalid={emailError ? "true" : "false"}
                    aria-describedby={emailError ? "resume-email-error" : undefined}
                    className="form-field mt-2 min-h-12 w-full rounded-lg px-4 py-3 text-base focus:outline-none"
                    autoComplete="email"
                  />
                  {emailError ? (
                    <p id="resume-email-error" className="mt-2 text-sm font-bold text-red-700" role="alert">
                      {emailError}
                    </p>
                  ) : null}
                </div>
                <button
                  type="button"
                  disabled={isSaving}
                  onClick={handleEmailSubmit}
                  className="focus-ring inline-flex min-h-12 items-center justify-center gap-2 rounded-lg bg-[#050505] px-5 text-sm font-extrabold text-[#FF6A2B] transition hover:bg-[#16120a] disabled:opacity-60"
                >
                  <Mail className="size-4" aria-hidden="true" />
                  {isSaving ? "Saving..." : "Email My Resume Link"}
                </button>
                {saveError ? (
                  <p className="text-sm font-bold text-red-700" role="alert">
                    {saveError}
                  </p>
                ) : null}
              </div>
            ) : (
              <SavedResumeDetails
                copyStatus={copyStatus}
                emailResult={emailResult}
                onCopy={handleCopyResumeLink}
                result={saveResult}
              />
            )}
          </section>
        </div>
      ) : null}
    </>
  );
}

function SavedResumeDetails({
  copyStatus,
  emailResult,
  onCopy,
  result,
}: {
  copyStatus: string;
  emailResult: ResumeEmailResult | null;
  onCopy: () => void | Promise<void>;
  result: SaveDraftResult;
}) {
  const deliverySucceeded = Boolean(emailResult?.emailSent);

  return (
    <div className="mt-5 grid gap-4">
      <p
        className={`rounded-lg border px-4 py-3 text-sm font-bold leading-7 ${
          deliverySucceeded
            ? "border-[#D94A16]/22 bg-[#fff9ef] text-[#5c5246]"
            : "border-red-200 bg-red-50 text-red-800"
        }`}
        role="status"
      >
        {deliverySucceeded
          ? "Your progress has been saved. A resume link has been sent to your email address. You may return at any time to continue where you left off."
          : emailResult?.message || "Email delivery failed. Please copy your resume link below."}
      </p>

      <div>
        <label htmlFor="resume-link" className="text-sm font-black text-[#16120a]">
          Resume link
        </label>
        <input
          id="resume-link"
          readOnly
          value={result.resumeLink}
          onFocus={(event) => event.currentTarget.select()}
          className="form-field mt-2 min-h-12 w-full rounded-lg px-3 py-3 text-sm focus:outline-none"
        />
      </div>

      <div className="grid gap-2 sm:grid-cols-2">
        <button
          type="button"
          onClick={onCopy}
          className="focus-ring inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-[#050505] px-4 text-sm font-extrabold text-[#FF6A2B] transition hover:bg-[#16120a]"
        >
          <Copy className="size-4" aria-hidden="true" />
          Copy Resume Link
        </button>
        <a
          href={result.resumeLink}
          className="focus-ring inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-[#D94A16]/35 bg-[#fff9ef] px-4 text-sm font-extrabold text-[#050505] transition hover:bg-white"
        >
          <ExternalLink className="size-4" aria-hidden="true" />
          View Resume Link
        </a>
      </div>

      {copyStatus ? <p className="text-sm font-bold text-[#5c5246]">{copyStatus}</p> : null}

      {result.draft.fileMeta.length ? (
        <div className="rounded-lg border border-[#D94A16]/20 bg-[#fff9ef] px-4 py-3 text-sm font-semibold leading-7 text-[#5c5246]">
          {result.draft.filesStored
            ? `Selected files saved in this browser: ${result.draft.fileMeta.map((file) => `${file.fileName} (${formatBytes(file.fileSize)})`).join(", ")}.`
            : "Your answers were saved, but this browser could not store the selected files. Please reselect files before final submission."}
        </div>
      ) : null}
    </div>
  );
}

export function formatBytes(bytes: number) {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** exponent;
  return `${value.toFixed(value >= 10 || exponent === 0 ? 0 : 1)} ${units[exponent]}`;
}
